import { createContext, useContext, useEffect, useCallback, ReactNode } from 'react'
import { themes, applyTheme, getThemeById, ThemeDef } from './themes'
import { useUserPref } from './hooks/useUserPref'

type Mode = 'dark' | 'light'

interface ThemeCtx {
  mode: Mode
  toggleMode: () => void
  styleId: string
  setStyleId: (id: string) => void
  currentTheme: ThemeDef
}

const defaultTheme = getThemeById('ocean') || themes[0]

const Ctx = createContext<ThemeCtx>({
  mode: 'dark',
  toggleMode: () => {},
  styleId: defaultTheme.id,
  setStyleId: () => {},
  currentTheme: defaultTheme,
})

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Server-side prefs replace localStorage. PrefsProvider обёрнут в main.tsx.
  const [mode, setMode] = useUserPref<Mode>('cb-theme', 'dark')
  const [styleId, setStyleIdState] = useUserPref<string>('cb-style', defaultTheme.id)

  const currentTheme = getThemeById(styleId) || defaultTheme

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(mode)
    root.setAttribute('data-theme', mode)
    applyTheme(currentTheme, mode)
  }, [mode, currentTheme])

  const toggleMode = useCallback(
    () => setMode(m => m === 'dark' ? 'light' : 'dark'),
    [setMode],
  )

  const setStyleId = useCallback(
    (id: string) => setStyleIdState(id),
    [setStyleIdState],
  )

  return (
    <Ctx.Provider value={{ mode, toggleMode, styleId, setStyleId, currentTheme }}>
      {children}
    </Ctx.Provider>
  )
}

export const useTheme = () => useContext(Ctx)

// backward compat
export type { Mode as Theme }
